import { Api } from '../typings/api';
import store from '../store';
import { StoreState } from '../reducers';
import Utils, { HttpHeader } from '.';
import userAgentUtils from './userAgent';
import UrlUtils from './url';
import { MultiAppAuthenticationInfosState, MultiAppAuthenticationCredentials } from '../containers/MultiAppAuthentication/reducer';
import { Server, AuthMethod } from '../containers/MultiAppAuthentication/constants';
import { Security } from '../containers/Authentication/reducer';
import { SharedData } from '../containers/Share/Panel';

export default class ApiUtils {

	private static get galleryConfig(): Api.Config {
		if (!store) {
			console.error('ApiUtils.galleryConfig() is called before store is created');
		}
		const { galleryInfos } = store.getState() as StoreState;
		if (!galleryInfos.isConfigLoaded) {
			console.error('ApiUtils.galleryConfig() is called before galleryInfos.config is loaded');
		}
		return galleryInfos.config;
	}

	private static get multiAppAuthenticationInfos(): MultiAppAuthenticationInfosState {
		if (!store) {
			console.error('ApiUtils.multiAppAuthenticationInfos() is called before store is created');
		}
		const { multiAppAuthenticationInfos } = store.getState() as StoreState;
		return multiAppAuthenticationInfos;
	}

	public static loginToAsp = (credentials: MultiAppAuthenticationCredentials): Promise<Api.AppLoginResponse> => {
		let loginData: {};
		let url: string;

		if (credentials.method === AuthMethod.Webpublication) {
			loginData = { login: credentials.login, password: credentials.password };
			url = UrlUtils.getLoginToAspUrl(ApiUtils.multiAppAuthenticationServer);
		} else {
			loginData = { org: credentials.company, token: credentials.token };
			url = UrlUtils.getOrgLoginUrl(ApiUtils.multiAppAuthenticationServer);
		}

		return Utils.fetchJson(url, 'POST', true, true, loginData);
	}

	public static get multiAppAuthenticationServer(): Server {
		return this.multiAppAuthenticationInfos.server as Server;
	}


	public static logoutFromAsp = (): Promise<{}> => {
		return Utils.fetchText(UrlUtils.getLogoutFromAspUrl(ApiUtils.multiAppAuthenticationServer), 'POST');
	}

	public static createAspDemoAccount = (email: string, password: string, phone: string): Promise<Api.CustomerAccount> => {
		const loginData = { email, password, phone };

		return Utils.fetchJson<Api.CustomerAccount>(UrlUtils.getCreateDemoAccountUrl(ApiUtils.multiAppAuthenticationServer), 'POST', true, true, loginData)
			.catch(error => Promise.reject(JSON.parse(error.responseText)));
	}

	public static getOrgAuthMethods = (company: string): Promise<Api.AuthMethodsResponse> => {
		return Utils.fetchJson<Api.AuthMethodsResponse>(UrlUtils.getAuthMethodsUrl(ApiUtils.multiAppAuthenticationServer), 'GET', true, true, { org: company })
			.catch(error => Promise.reject(JSON.parse(error.responseText)));
	}

	// TODO: change return type of authenticate
	public static authenticate = (user: string, pass: string): Promise<{} | void> => {
		// HACK for IOS webviews for call with basic Authentication which can reply with a 401, https://stackoverflow.com/a/13018549
		const isAsync = !userAgentUtils.isIOSApp();

		return Utils.fetchJson(UrlUtils.getLoginUrl(ApiUtils.galleryConfig, user, pass, Security.Excel), 'GET', isAsync);
	}

	public static getSecurityHeaderIfNeeded = (): HttpHeader[] => {
		const { authentication } = store.getState() as StoreState;
		const { mode } = authentication.userInfos;
		const isSamlAuthenticationType = mode && mode.method === Security.Saml;
		return isSamlAuthenticationType
			? [{
				key: 'Access-Control-Allow-Credentials',
				value: 'true',
			}]
			: [];
	}

	public static getSecurityType = () => {
		const galleryConfig = ApiUtils.galleryConfig;
		const url = UrlUtils.getLoginJsonUrl(galleryConfig);
		// HACK for IOS webviews for call with basic Authentication which can reply with a 401, https://stackoverflow.com/a/13018549
		const isAsync = !userAgentUtils.isIOSApp();

		return Utils.promiseXHR('GET', url, [], isAsync)
			.then(response => {
				const securityType = response.getResponseHeader('Security') as Security;
				const samlUrl = response.getResponseHeader('SAML_URL') || '';

				return securityType
					? Promise.resolve({securityType, samlUrl})
					: Promise.resolve(undefined);
			})
			.catch(error => {
				return Promise.reject(error);
			});
	}

	public static getTemplate = (server?: Server): Promise<Api.Template> => {
		// HACK for IOS webviews for call with basic Authentication which can reply with a 401, https://stackoverflow.com/a/13018549
		const isAsync = !userAgentUtils.isIOSApp();

		return Utils.fetchJson(UrlUtils.getTemplateUrl(ApiUtils.galleryConfig, server), 'GET', isAsync);
	}

	public static getConfig = (clientId: number, galleryId: number): Promise<Api.Config> => {
		return Utils.fetchJson(UrlUtils.getConfigUrl(ApiUtils.multiAppAuthenticationServer, clientId, galleryId), 'GET', true, true);
	}

	public static getStatistics = (): Promise<string> => {
		return Utils.fetchText(UrlUtils.getStatisticsUrl(ApiUtils.galleryConfig));
	}

	public static getResolutions = (): Promise<Api.ResolutionGroup> => {
		return Utils.fetchJson(UrlUtils.getResolutionsUrl(ApiUtils.galleryConfig));
	}

	public static getChildren = (driveItemId?: number): Promise<Api.Outcoming> | void => {
		const galleryConfig = ApiUtils.galleryConfig;

		const resolvedDriveItemId = driveItemId || galleryConfig.applicationGallery;
		return Utils.fetchJson(UrlUtils.getChildrenUrl(ApiUtils.galleryConfig, resolvedDriveItemId));
	}

	public static getMeta = (driveItemId: number, isAsync: boolean = true): Promise<Api.DriveFolderInfo> => {
		return Utils.fetchJson(UrlUtils.getMetaUrl(ApiUtils.galleryConfig, driveItemId), 'GET', isAsync);
	}

	public static getContentBlock = (driveItemId: number, block: number): Promise<Api.DriveItemContentBlock> => {
		return Utils.fetchJson(UrlUtils.getContentBlockUrl(ApiUtils.galleryConfig, driveItemId, block));
	}

	public static getNewContents = (sinceDate: Date): Promise<Api.DriveItemContentBlock> => {
		return Utils.fetchJson(UrlUtils.getNewContentsUrl(ApiUtils.galleryConfig, sinceDate));
	}

	public static getSearch = (searchString: string): Promise<Api.DriveItemContentBlock> => {
		return Utils.fetchJson(UrlUtils.getSearchUrl(ApiUtils.galleryConfig, searchString));
	}

	public static getOutcomings = (clientId: number, driveItemId: number): Promise<Api.Outcoming> => {
		return Utils.fetchJson(UrlUtils.getOutcomingsUrl(ApiUtils.galleryConfig, driveItemId));
	}

	public static getAvailableServers = (): Promise<{ [k: string]: Api.ServerInfos }> => {
		return Utils.fetchJson<Api.GetClosestServerResponse>('https://fr.adminzone-secure.net/services/rest/loginWs/getClosestServer')
			.then(res => res.servers);
	}

	public static shareContent = (requestData: SharedData) => {
		const url = UrlUtils.getShareServiceUrl(ApiUtils.galleryConfig);
		return Utils.fetchText(url, 'POST', true, true, {}, JSON.stringify(requestData)); // requestData needs to be stringified
	}
}



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/utils/api.ts